import { useEffect, useState, useMemo } from "react";
import { useIdentity } from "base/identity-resolver";
import {
  createVoipUserSettings,
  getVoipUserSettingsByOwnerUserId,
  updateVoipUserSettings,
  validateUserPhoneNumber,
  validationNumberRequest,
} from "../utils/api-requests/hooks/use-call-settings-requests";
import EverTrue from "app";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const useCallSettings = () => {
  //voip/call settings states
  const [callerIdNumber, setCallerIdNumber] = useState("");
  const [userSettingsRecordId, setUserSettingsRecordId] = useState(false);
  const [validated, setValidated] = useState(false);
  const [callSid, setCallSid] = useState();
  const [validationCode, setValidationCode] = useState("");
  const { user, org } = useIdentity();
  const payload = useMemo(buildPayload, [callerIdNumber, user.id, org.id]);

  // Build Payload
  function buildPayload() {
    return {
      caller_id_phone_number: callerIdNumber,
      owner_user_id: user.id,
      oid: org.id,
    };
  }

  useEffect(() => {
    // check if user already has a settings record. if they do:
    // populate the form with existing values
    // use the exiting ID in UPDATE request (do not create a new record);
    const getAndSetCurrentUserSettings = async () => {
      const res = await getVoipUserSettingsByOwnerUserId(user.id); // will return null if user does not have settings saved.
      if (res) {
        const { caller_id_phone_number = "", id = null } = res;

        setCallerIdNumber(caller_id_phone_number);
        setUserSettingsRecordId(id);
      }
    };

    getAndSetCurrentUserSettings();
  }, [user.id]);

  const handleSubmit = async () => {
    if (userSettingsRecordId) await updateVoipUserSettings(payload, userSettingsRecordId);
    else {
      const { id } = await createVoipUserSettings(payload);
      setUserSettingsRecordId(id);
    }
  };

  const validatePhoneNumber = async (callerNumber, openValidationModal) => {
    const { validationCode, errorMsg, callSid, success } = await validateUserPhoneNumber(callerNumber);
    if (success) {
      setCallSid(callSid);
      setValidationCode(validationCode);
      openValidationModal();
      // automatically check validation status after 5 seconds
      // in case anyone closes the modal right away.
      await wait(3000);
      checkValidationStatus(callSid, 3000);
    } else {
      EverTrue.Alert.error(errorMsg);
    }
  };

  const checkValidationStatus = async (callSid, delay = 1000, closeValidationModal = () => {}) => {
    let isValidated = false;
    let response = null;
    let attempt = 1;
    closeValidationModal();
    if (!validated) {
      // try to validate 3 times, with a 1 second delay between each attempt
      while (attempt <= 3 && !response) {
        await wait(delay); // delay for API's sake.
        response = await validationNumberRequest(callSid); // will be null or an object.
        if (response) {
          isValidated = response.successfullyValidated;
        }
        attempt++;
      }
      setValidated(isValidated); // update hook state.
    }
  };

  return {
    callerIdNumber,
    setCallerIdNumber,
    handleSubmit,
    validatePhoneNumber,
    validated,
    callSid,
    validationCode,
    checkValidationStatus,
  };
};

export default useCallSettings;
